/**
 * Modelo de formulario y validaciones de Membresías (US-2.4) — Admin Web.
 *
 * La red y el contrato viven en `membershipApi`/`@goelectric/shared-types`; acá está
 * el estado editable de la vista y la conversión al body del contrato.
 *
 * Reglas acordadas con US 2.4 (alineadas con la validación del backend):
 * - `name` obligatorio, máximo 200 caracteres.
 * - `billingCycle` MONTHLY|ANNUAL (obligatorio).
 * - `priceCop` >= 0 y dentro de NUMERIC(12,2).
 * - `benefits` es una lista dinámica de strings; las filas vacías se descartan.
 * - `tariffDiscountPct` entre 0 y 100 (default 0).
 * - `active` por defecto true; el `id` nunca se muestra ni se envía.
 */
import type {
  CreateMembershipBody,
  Membership,
  MembershipBillingCycle,
  UpdateMembershipBody,
} from '@goelectric/shared-types';

/* ========================================================================= */
/* Constantes del formulario                                                 */
/* ========================================================================= */

/** Nombre máximo aceptado por el backend. */
export const MEMBERSHIP_NAME_MAX = 200;

/** Descripción máxima aceptada por el backend. */
export const MEMBERSHIP_DESCRIPTION_MAX = 2000;

/** Cantidad máxima de beneficios aceptada por el backend. */
export const MEMBERSHIP_BENEFITS_MAX = 50;

/** Precio máximo aceptado por la columna NUMERIC(12,2). */
export const MEMBERSHIP_PRICE_MAX = 9_999_999_999.99;

/** Descuento porcentual mínimo. */
export const MEMBERSHIP_DISCOUNT_MIN = 0;

/** Descuento porcentual máximo. */
export const MEMBERSHIP_DISCOUNT_MAX = 100;

/** Moneda fija del catálogo (el contrato la devuelve; no se edita). */
export const MEMBERSHIP_DEFAULT_CURRENCY = 'COP';

/** Longitud máxima de un beneficio (evita filas accidentales gigantes). */
export const MEMBERSHIP_BENEFIT_MAX = 200;

/** Ciclos válidos del contrato. */
export const MEMBERSHIP_BILLING_CYCLES: readonly MembershipBillingCycle[] = [
  'MONTHLY',
  'ANNUAL',
];

/* ========================================================================= */
/* Estado del formulario                                                     */
/* ========================================================================= */

/** Valores editables; los numéricos admiten `null` mientras el usuario no escribe. */
export interface MembershipFormValues {
  name: string;
  description?: string | null;
  billingCycle: MembershipBillingCycle;
  priceCop?: number | null;
  benefits?: string[];
  tariffDiscountPct?: number | null;
  active: boolean;
}

/** Estado inicial del alta: mensual, activa, sin descuento y sin beneficios. */
export function emptyMembershipFormValues(): MembershipFormValues {
  return {
    name: '',
    description: '',
    billingCycle: 'MONTHLY',
    priceCop: null,
    benefits: [],
    tariffDiscountPct: 0,
    active: true,
  };
}

/** Mapea la entidad del contrato al estado del formulario de edición. */
export function membershipToFormValues(membership: Membership): MembershipFormValues {
  return {
    name: membership.name,
    description: membership.description ?? '',
    billingCycle: membership.billingCycle,
    priceCop: membership.priceCop,
    benefits: membership.benefits ?? [],
    tariffDiscountPct: membership.tariffDiscountPct ?? 0,
    active: membership.active,
  };
}

/* ========================================================================= */
/* Conversión al body del contrato                                           */
/* ========================================================================= */

/** Normaliza la lista de beneficios: recorta, descarta vacíos y conserva el orden. */
export function normalizeBenefits(benefits: string[] | null | undefined): string[] {
  return (benefits ?? [])
    .map((benefit) => (benefit ?? '').trim())
    .filter((benefit) => benefit !== '');
}

/** Descripción opcional: `null` cuando queda vacía. */
export function normalizeDescription(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * El precio es obligatorio. No se cae a 0 de forma silenciosa: un `null`/`undefined`
 * fuera del formulario ya validado debe fallar, no crear una membresía gratuita.
 */
function requirePriceCop(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    throw new Error('priceCop es obligatorio y debe ser un número finito');
  }
  return value;
}

/** Body de `POST /api/v1/memberships` (el id lo genera el backend). */
export function toCreateMembershipBody(
  values: MembershipFormValues,
): CreateMembershipBody {
  const description = normalizeDescription(values.description);
  return {
    name: values.name.trim(),
    ...(description !== null ? { description } : {}),
    billingCycle: values.billingCycle,
    priceCop: requirePriceCop(values.priceCop),
    benefits: normalizeBenefits(values.benefits),
    tariffDiscountPct: values.tariffDiscountPct ?? 0,
    active: values.active,
  };
}

/**
 * Body de `PATCH /api/v1/memberships/{membershipId}`. Se envía el formulario completo
 * para que la edición sea predecible; `description` vacía se envía como `null`.
 */
export function toUpdateMembershipBody(
  values: MembershipFormValues,
): UpdateMembershipBody {
  const body: UpdateMembershipBody = {
    name: values.name.trim(),
    description: normalizeDescription(values.description),
    billingCycle: values.billingCycle,
    priceCop: requirePriceCop(values.priceCop),
    benefits: normalizeBenefits(values.benefits),
    tariffDiscountPct: values.tariffDiscountPct ?? 0,
    active: values.active,
  };
  return body;
}

/* ========================================================================= */
/* Validaciones                                                              */
/* ========================================================================= */

/** Problema de validación asociado a un campo del formulario. */
export interface MembershipValidationIssue {
  field: string;
  message: string;
}

/** Path de antd (`NamePath`) derivado de una ruta simple. */
export type MembershipFieldPath = (string | number)[];

/** Convierte una ruta simple en el path que `form.setFields` entiende. */
export function toAntdFieldPath(field: string): MembershipFieldPath {
  return field.split('.').filter(Boolean);
}

/** Valida el formulario completo; devuelve los problemas con su campo. */
export function validateMembershipFormValues(
  values: MembershipFormValues,
): MembershipValidationIssue[] {
  const issues: MembershipValidationIssue[] = [];
  const name = values.name?.trim() ?? '';

  if (!name) {
    issues.push({ field: 'name', message: 'Escribe el nombre de la membresía.' });
  } else if (name.length > MEMBERSHIP_NAME_MAX) {
    issues.push({
      field: 'name',
      message: `El nombre no puede superar ${MEMBERSHIP_NAME_MAX} caracteres.`,
    });
  }

  if (!MEMBERSHIP_BILLING_CYCLES.includes(values.billingCycle)) {
    issues.push({
      field: 'billingCycle',
      message: 'Elige el ciclo de facturación (mensual o anual).',
    });
  }

  if (values.priceCop === undefined || values.priceCop === null) {
    issues.push({ field: 'priceCop', message: 'Ingresa el precio de la membresía.' });
  } else if (values.priceCop < 0) {
    issues.push({ field: 'priceCop', message: 'El precio debe ser mayor o igual a 0.' });
  } else if (values.priceCop > MEMBERSHIP_PRICE_MAX) {
    issues.push({
      field: 'priceCop',
      message: `El precio no puede superar ${MEMBERSHIP_PRICE_MAX}.`,
    });
  }

  const discount = values.tariffDiscountPct ?? 0;
  if (
    discount < MEMBERSHIP_DISCOUNT_MIN ||
    discount > MEMBERSHIP_DISCOUNT_MAX ||
    Number.isNaN(discount)
  ) {
    issues.push({
      field: 'tariffDiscountPct',
      message: 'El descuento debe estar entre 0 y 100.',
    });
  }

  const description = (values.description ?? '').trim();
  if (description.length > MEMBERSHIP_DESCRIPTION_MAX) {
    issues.push({
      field: 'description',
      message: `La descripción no puede superar ${MEMBERSHIP_DESCRIPTION_MAX} caracteres.`,
    });
  }

  const benefits = values.benefits ?? [];
  if (benefits.length > MEMBERSHIP_BENEFITS_MAX) {
    issues.push({
      field: 'benefits',
      message: `No puedes agregar más de ${MEMBERSHIP_BENEFITS_MAX} beneficios.`,
    });
  }
  benefits.forEach((benefit, index) => {
    if ((benefit ?? '').trim().length > MEMBERSHIP_BENEFIT_MAX) {
      issues.push({
        field: `benefits.${index}`,
        message: `Cada beneficio no puede superar ${MEMBERSHIP_BENEFIT_MAX} caracteres.`,
      });
    }
  });

  return issues;
}
